// src/components/demo/DemoSignaturePad.js
//
// Finger-drawn signature canvas (SPEC §7.3). Pointer events, ink-on-paper,
// exports a PNG data URL (~600×240 backing store). The data URL travels
// inline on the waiver doc; the Cloud Function moves it to Storage.

import React, { useEffect, useRef, useState } from 'react';

const CANVAS_W = 600;
const CANVAS_H = 240;

export default function DemoSignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = '#23262B'; // foam ink
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const toCanvasPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const pointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastRef.current = toCanvasPoint(e);
  };

  const pointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pt = toCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastRef.current = pt;
    if (!hasInk) setHasInk(true);
  };

  const pointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange && onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    setHasInk(false);
    onChange && onChange(null);
  };

  // Design (DWaiver): SIGN HERE mono hint bottom-left, orange Clear top-right,
  // both overlaid on the square-cornered canvas.
  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        className="w-full block"
        style={{
          aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
          background: '#FFFFFF',
          border: '1.5px solid var(--foam-line)',
          touchAction: 'none',
        }}
      />
      {!hasInk && (
        <div
          className="absolute font-mono pointer-events-none"
          style={{ left: 14, bottom: 8, fontSize: 10, letterSpacing: '0.1em', color: 'var(--foam-mute)' }}
        >
          SIGN HERE
        </div>
      )}
      {hasInk && (
        <button
          type="button"
          onClick={clear}
          className="absolute font-sans font-semibold text-[12px]"
          style={{ right: 12, top: 10, color: 'var(--foam-orange)', minHeight: 32 }}
        >
          Clear
        </button>
      )}
    </div>
  );
}
